import {
  ABNORMAL_COLORS,
  type AbnormalStyle,
  type ComputedSection,
  type ComputedTestRow,
  DEFAULT_ABNORMAL,
  type ReportSection,
  resolveAbnormal,
  type TestRow,
} from '@quickreport/domain';
import { Textarea } from '@quickreport/ui';
import clsx from 'clsx';
import { Palette } from 'lucide-react';
import { useRef, useState } from 'react';
import { ResultStyleDialog, type StyleChoice } from './ResultStyleDialog';

interface Props {
  section: ReportSection;
  computed: ComputedSection;
  editable: boolean;
  onValue: (rowId: string, value: string) => void;
  onNarrative: (rowId: string, text: string) => void;
  /** The lab's setting for out-of-range values, and this report's own choice, if any. */
  lab?: { abnormalBold?: boolean | undefined; abnormalColor?: AbnormalStyle['color'] };
  report?: AbnormalStyle | undefined;
  /** Save how a result (or the whole report) prints when it is out of range. */
  onStyle?: (rowId: string, choice: StyleChoice) => void;
}

/**
 * Result entry for one template. Type a value and press Enter to jump to the next result
 * (calculated rows are skipped). Out-of-range values turn red as you type.
 */
export function ResultTable({
  section,
  computed,
  editable,
  onValue,
  onNarrative,
  lab,
  report,
  onStyle,
}: Props) {
  const [styling, setStyling] = useState<string | null>(null);
  const inputs = useRef<Map<string, HTMLInputElement | HTMLSelectElement>>(new Map());
  const order = computed.rows.flatMap((r) =>
    r.kind === 'test' && !r.calculated ? [r.row.id] : [],
  );

  function focusNext(from: string, back = false) {
    const i = order.indexOf(from);
    const next = order[i + (back ? -1 : 1)];
    const el = next ? inputs.current.get(next) : undefined;
    el?.focus();
    if (el && 'select' in el) el.select();
  }

  const key = (id: string) => (el: HTMLInputElement | HTMLSelectElement | null) => {
    if (el) inputs.current.set(id, el);
    else inputs.current.delete(id);
  };
  const onKey = (id: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || (e.key === 'ArrowDown' && !e.altKey)) {
      e.preventDefault();
      focusNext(id);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusNext(id, true);
    }
  };

  const stylingRow = styling
    ? computed.rows.find((r) => r.kind === 'test' && r.row.id === styling)
    : undefined;
  const own = styling ? section.rowStyles?.[styling] : undefined;
  const dialog =
    styling && stylingRow && stylingRow.kind === 'test' && onStyle ? (
      <ResultStyleDialog
        label={stylingRow.row.label}
        lab={{
          bold: lab?.abnormalBold ?? DEFAULT_ABNORMAL.bold,
          color: lab?.abnormalColor ?? DEFAULT_ABNORMAL.color,
        }}
        inherited={(() => {
          const x = resolveAbnormal(lab, report, undefined);
          return { bold: x.bold, color: x.color };
        })()}
        inheritedFrom={resolveAbnormal(lab, report, undefined).from === 'report' ? 'report' : 'lab'}
        own={
          own && (own.bold !== undefined || own.color !== undefined)
            ? (() => {
                const x = resolveAbnormal(lab, report, own);
                return { bold: x.bold, color: x.color };
              })()
            : null
        }
        onClose={() => setStyling(null)}
        onSave={(c) => {
          onStyle(styling, c);
          setStyling(null);
        }}
      />
    ) : null;

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-paper">
      {dialog}
      <table className="w-full border-collapse text-left max-md:block">
        <thead className="bg-canvas text-[12px] tracking-wide text-slate uppercase max-md:hidden">
          <tr>
            <th className="px-3 py-2 font-medium">Investigation</th>
            <th className="w-44 px-3 py-2 font-medium">Result</th>
            <th className="w-12 px-1 py-2 text-center font-medium">Flag</th>
            <th className="w-28 px-3 py-2 font-medium">Unit</th>
            <th className="px-3 py-2 font-medium">Reference</th>
          </tr>
        </thead>
        <tbody className="max-md:block">
          {computed.rows.map((r) => {
            if (r.kind !== 'test' && r.kind === 'section') {
              return (
                <tr key={r.id} className="border-t border-line bg-canvas/60 max-md:block">
                  <td
                    colSpan={5}
                    className="px-3 py-1.5 text-[12px] font-bold tracking-wide uppercase max-md:block"
                  >
                    {r.text}
                  </td>
                </tr>
              );
            }
            if (r.kind !== 'test' && r.kind === 'note') {
              return (
                <tr key={r.id} className="border-t border-line max-md:block">
                  <td
                    colSpan={5}
                    className="px-3 py-1.5 text-[13px] text-slate italic max-md:block"
                  >
                    {r.text}
                  </td>
                </tr>
              );
            }
            if (r.kind !== 'test') {
              return (
                <tr key={r.id} className="border-t border-line max-md:block">
                  <td colSpan={5} className="px-3 py-2 max-md:block">
                    <Textarea
                      aria-label="Narrative text"
                      rows={4}
                      disabled={!editable}
                      value={section.narratives?.[r.id] ?? r.text}
                      onChange={(e) => onNarrative(r.id, e.target.value)}
                    />
                  </td>
                </tr>
              );
            }
            return (
              <Row
                key={r.row.id}
                r={r}
                value={section.values[r.row.id] ?? ''}
                editable={editable}
                setRef={key(r.row.id)}
                onKey={onKey(r.row.id)}
                onValue={(v) => onValue(r.row.id, v)}
                {...(editable && onStyle
                  ? {
                      look: resolveAbnormal(lab, report, section.rowStyles?.[r.row.id]),
                      onStyle: () => setStyling(r.row.id),
                    }
                  : {})}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  r,
  value,
  editable,
  setRef,
  onKey,
  onValue,
  look,
  onStyle,
}: {
  r: ComputedTestRow;
  value: string;
  editable: boolean;
  setRef: (el: HTMLInputElement | HTMLSelectElement | null) => void;
  onKey: (e: React.KeyboardEvent) => void;
  onValue: (v: string) => void;
  /** How this result prints when out of range, and the way to change it. */
  look?: ReturnType<typeof resolveAbnormal>;
  onStyle?: () => void;
}) {
  const t: TestRow = r.row;
  const flagged = Boolean(r.flag.code);
  const issue = r.issues.find((i) => i.kind === 'not_a_number' || i.kind === 'required');
  return (
    <tr
      className={clsx(
        'border-t border-line/70',
        // On a phone each test is a small card: name and result side by side, the rest beneath.
        'max-md:grid max-md:grid-cols-[minmax(0,1fr)_8.5rem] max-md:items-start max-md:gap-x-3 max-md:px-3 max-md:py-2.5',
        r.flag.critical && 'bg-red-50',
      )}
    >
      <td className="px-3 py-1.5 max-md:col-start-1 max-md:row-start-1 max-md:px-0 max-md:py-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{t.label}</span>
          {look && onStyle ? (
            <button
              type="button"
              onClick={onStyle}
              aria-label={`How ${t.label} prints when it is out of range`}
              title={
                look.from === 'default' || look.from === 'lab'
                  ? 'How this prints if it is out of range'
                  : look.from === 'report'
                    ? 'This report prints out-of-range values differently'
                    : 'This result prints differently'
              }
              className={clsx(
                'relative grid h-6 w-6 shrink-0 place-items-center rounded-md hover:bg-brand-soft',
                look.from === 'result' || look.from === 'report'
                  ? 'text-brand'
                  : 'text-slate/60 hover:text-brand',
              )}
            >
              <Palette size={14} />
              {look.from === 'result' || look.from === 'report' ? (
                <span
                  className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ring-1 ring-white"
                  style={{ background: ABNORMAL_COLORS[look.color].hex }}
                />
              ) : null}
            </button>
          ) : null}
        </div>
        <div className="text-[11px] text-slate">
          {[t.method, r.calculated ? 'Calculated' : null].filter(Boolean).join(' · ')}
        </div>
      </td>
      <td className="px-3 py-1 max-md:col-start-2 max-md:row-start-1 max-md:px-0 max-md:py-0">
        {r.calculated ? (
          <div
            className={clsx(
              'tabular px-2 py-1 text-[14px]',
              flagged ? 'font-bold text-flag' : 'text-slate',
            )}
          >
            {r.display || (
              <span className="text-[12px] text-slate/70" title={r.issues[0]?.message}>
                {r.issues[0]?.message ?? '—'}
              </span>
            )}
          </div>
        ) : t.valueType === 'option' && t.allowOther ? (
          // Pick from the list or type anything else: the same box as Department in the template
          // editor, for results such as "Nil" or a count.
          <>
            <input
              ref={setRef}
              list={`choices-${t.id}`}
              aria-label={`Result for ${t.label}`}
              disabled={!editable}
              value={value}
              placeholder={
                (t.options ?? [])[0] ? `${(t.options ?? [])[0]} or type` : 'Pick or type'
              }
              onChange={(e) => onValue(e.target.value)}
              onKeyDown={onKey}
              autoComplete="off"
              className={clsx(
                'h-8 w-full rounded-md border border-line bg-paper px-2 outline-none placeholder:text-slate/50 focus:border-brand focus:ring-2 focus:ring-brand/15 max-md:h-11 max-md:text-base',
                flagged && 'font-bold text-flag',
              )}
            />
            <datalist id={`choices-${t.id}`}>
              {(t.options ?? []).map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
          </>
        ) : t.valueType === 'option' ? (
          <select
            ref={setRef}
            aria-label={`Result for ${t.label}`}
            disabled={!editable}
            value={value}
            onChange={(e) => onValue(e.target.value)}
            onKeyDown={onKey}
            className={clsx(
              'h-8 w-full rounded-md border border-line bg-paper px-1.5 max-md:h-11 max-md:text-base',
              flagged && 'font-bold text-flag',
            )}
          >
            <option value="" />
            {(t.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={setRef}
            aria-label={`Result for ${t.label}`}
            aria-invalid={Boolean(issue)}
            disabled={!editable}
            value={value}
            inputMode={t.valueType === 'numeric' ? 'decimal' : 'text'}
            onChange={(e) => onValue(e.target.value)}
            onKeyDown={onKey}
            autoComplete="off"
            className={clsx(
              'tabular h-8 w-full rounded-md border bg-paper px-2 outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 max-md:h-11 max-md:text-base',
              issue?.kind === 'not_a_number' ? 'border-flag' : 'border-line',
              flagged && 'font-bold text-flag',
            )}
          />
        )}
        {r.flag.band ? (
          <div className={clsx('px-2 text-[11px]', flagged ? 'text-flag' : 'text-slate')}>
            {r.flag.band}
          </div>
        ) : null}
        {issue?.kind === 'not_a_number' ? (
          <div className="px-2 text-[11px] text-flag">{issue.message}</div>
        ) : null}
      </td>
      <td className="px-1 text-center font-bold text-flag max-md:col-start-2 max-md:row-start-2 max-md:px-0 max-md:text-right">
        {r.flag.code}
        {r.flag.critical ? <span className="sr-only"> critical</span> : null}
      </td>
      <td className="px-3 text-slate max-md:col-start-1 max-md:row-start-2 max-md:px-0 max-md:text-[13px]">
        {t.unit}
      </td>
      <td className="px-3 text-[13px] whitespace-pre-line text-slate max-md:col-span-2 max-md:row-start-3 max-md:px-0 max-md:text-[12px]">
        {r.refText ? <span className="md:hidden">Reference: </span> : null}
        {r.refText}
      </td>
    </tr>
  );
}
